import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import HelpIcon from '../components/HelpIcon';
import { 
  Store, Search, Plus, Star, Phone, Mail, Globe, MapPin, User, 
  CreditCard, Truck, Clock, FileText, MoreVertical, Edit2, Trash2,
  Package, DollarSign, ExternalLink, ChevronUp, ChevronDown, Filter,
  Building, UserCircle, RefreshCw, Eye, ShoppingCart, Link2, ArrowLeft,
  Sun, Moon
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_TERMS = [
  'COD', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90', 
  'Prepaid', '50% Deposit', 'Progress Billing', 'Upon Completion'
];

const SHIPPING_METHODS = [
  'Will Call', 'Ground', 'Freight - Standard', 'Freight - Expedited',
  'White Glove', 'Vendor Delivery', 'Third Party', 'Customer Pickup'
];

export default function VendorsPage() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [specCategories, setSpecCategories] = useState([]);
  const [specCategoryFilter, setSpecCategoryFilter] = useState('all');
  
  // Sorting & Grouping
  const [sortBy, setSortBy] = useState('company');
  const [sortOrder, setSortOrder] = useState('asc');
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialog states
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  
  // Form state
  const [vendorForm, setVendorForm] = useState({
    first_name: '',
    last_name: '',
    company: '',
    job_title: '',
    email: '',
    phone: '',
    phone_mobile: '',
    website: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    notes: '',
    spec_categories: [],
    is_vendor: true,
    vendor_account_number: '',
    payment_terms: '',
    preferred_shipping: '',
    minimum_order: '',
    lead_times: {},
    return_policy: '',
    rep_name: '',
    rep_email: '',
    rep_phone: '',
    is_favorite_vendor: false,
    vendor_rating: 0,
  });
  
  // Pricing form
  const [pricingForm, setPricingForm] = useState({
    clip_name: '',
    price: '',
    unit: 'each',
    notes: '',
    quote_number: ''
  });

  useEffect(() => {
    fetchVendors();
    fetchSpecCategories();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/vendors`);
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/spec-categories`);
      setSpecCategories(response.data?.categories || response.data || []);
    } catch (error) {
      console.error('Error fetching spec categories:', error);
    }
  };

  const openNewVendor = () => {
    setEditingVendor(null);
    setVendorForm({
      first_name: '',
      last_name: '',
      company: '',
      job_title: '',
      email: '',
      phone: '',
      phone_mobile: '',
      website: '',
      address_street: '',
      address_city: '',
      address_state: '',
      address_zip: '',
      notes: '',
      spec_categories: [],
      is_vendor: true,
      vendor_account_number: '',
      payment_terms: '',
      preferred_shipping: '',
      minimum_order: '',
      lead_times: {},
      return_policy: '',
      rep_name: '',
      rep_email: '',
      rep_phone: '',
      is_favorite_vendor: false,
      vendor_rating: 0,
    });
    setShowVendorDialog(true);
  };

  const openEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      first_name: vendor.first_name || '',
      last_name: vendor.last_name || '',
      company: vendor.company || '',
      job_title: vendor.job_title || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      phone_mobile: vendor.phone_mobile || '',
      website: vendor.website || '',
      address_street: vendor.address_street || '',
      address_city: vendor.address_city || '',
      address_state: vendor.address_state || '',
      address_zip: vendor.address_zip || '',
      notes: vendor.notes || '',
      spec_categories: vendor.spec_categories || [],
      is_vendor: true,
      vendor_account_number: vendor.vendor_account_number || '',
      payment_terms: vendor.payment_terms || '',
      preferred_shipping: vendor.preferred_shipping || '',
      minimum_order: vendor.minimum_order || '',
      lead_times: vendor.lead_times || {},
      return_policy: vendor.return_policy || '',
      rep_name: vendor.rep_name || '',
      rep_email: vendor.rep_email || '',
      rep_phone: vendor.rep_phone || '',
      is_favorite_vendor: vendor.is_favorite_vendor || false,
      vendor_rating: vendor.vendor_rating || 0,
    });
    setShowVendorDialog(true);
  };

  const saveVendor = async () => {
    try {
      const data = {
        ...vendorForm,
        category: 'Vendor/Supplier',
        is_vendor: true,
        minimum_order: vendorForm.minimum_order ? parseFloat(vendorForm.minimum_order) : null,
      };
      
      if (editingVendor) {
        await axios.put(`${API}/api/contacts/${editingVendor.id}`, data);
        toast.success('Vendor updated');
      } else {
        await axios.post(`${API}/api/contacts`, data);
        toast.success('Vendor created');
      }
      setShowVendorDialog(false);
      fetchVendors();
    } catch (error) {
      console.error('Error saving vendor:', error);
      toast.error('Failed to save vendor');
    }
  };

  const deleteVendor = async (vendor) => {
    if (!window.confirm(`Delete vendor "${vendor.company || vendor.first_name}"?`)) return;
    try {
      await axios.delete(`${API}/api/contacts/${vendor.id}`);
      toast.success('Vendor deleted');
      fetchVendors();
    } catch (error) {
      console.error('Error deleting vendor:', error);
      toast.error('Failed to delete vendor');
    }
  };

  const toggleFavorite = async (vendor) => {
    try {
      await axios.put(`${API}/api/contacts/${vendor.id}`, { 
        is_favorite_vendor: !vendor.is_favorite_vendor 
      });
      toast.success(vendor.is_favorite_vendor ? 'Removed from favorites' : 'Added to favorites');
      fetchVendors();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorite');
    }
  };

  const viewVendorDetail = (vendor) => {
    setSelectedVendor(vendor);
    setShowDetailSheet(true);
  };

  const addPricingEntry = async () => {
    if (!selectedVendor) return;
    try {
      await axios.put(`${API}/api/vendors/${selectedVendor.id}/add-pricing`, pricingForm);
      toast.success('Pricing added');
      setShowPricingDialog(false);
      setPricingForm({ clip_name: '', price: '', unit: 'each', notes: '', quote_number: '' });
      // Refresh vendor detail
      const response = await axios.get(`${API}/api/vendors/${selectedVendor.id}`);
      setSelectedVendor(response.data);
    } catch (error) {
      console.error('Error adding pricing:', error);
      toast.error('Failed to add pricing');
    }
  };

  // Filtering & Sorting
  const filteredVendors = vendors.filter(v => {
    const matchesSearch = searchQuery === '' ||
      (v.company || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (v.first_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (v.last_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (v.rep_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSpecCategory = specCategoryFilter === 'all' ||
      (v.spec_categories && v.spec_categories.includes(specCategoryFilter));
    
    // Tab filtering
    if (activeTab === 'favorites') return matchesSearch && matchesSpecCategory && v.is_favorite_vendor;
    
    return matchesSearch && matchesSpecCategory;
  }).sort((a, b) => {
    // Favorites first
    if (a.is_favorite_vendor && !b.is_favorite_vendor) return -1;
    if (!a.is_favorite_vendor && b.is_favorite_vendor) return 1;
    
    let comparison = 0;
    switch (sortBy) {
      case 'company':
        comparison = (a.company || '').localeCompare(b.company || '');
        break;
      case 'rating':
        comparison = (b.vendor_rating || 0) - (a.vendor_rating || 0);
        break;
      default:
        comparison = (a.company || '').localeCompare(b.company || '');
    }
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  // Stats
  const stats = {
    total: vendors.length,
    favorites: vendors.filter(v => v.is_favorite_vendor).length,
    withAccount: vendors.filter(v => v.vendor_account_number).length,
  };

  const getInitials = (vendor) => {
    if (vendor.company) return vendor.company.substring(0, 2).toUpperCase();
    return `${(vendor.first_name || 'V')[0]}${(vendor.last_name || 'E')[0]}`.toUpperCase();
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <Star 
            key={star} 
            className={`w-3 h-3 ${star <= (rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} 
          />
        ))}
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-[#0D0D0D]' : 'bg-gray-50'}`}>
      {/* Header Bar */}
      <div className={`border-b px-6 py-3 flex items-center justify-between ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white'}`}>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
              <Store className="w-5 h-5 text-purple-500" />
            </div>
            <div className="flex items-center gap-2">
              <HelpIcon elementId="vendors-page" position="inline" />
              <div>
                <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : ''}`}>Vendor Management</h1>
                <p className="text-sm text-muted-foreground">Manage suppliers, pricing, and purchase orders</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => {
            setDarkMode(!darkMode);
            document.documentElement.classList.toggle('dark');
          }}>
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </div>
      
      <main className="p-6 max-w-7xl mx-auto">
        {/* Action Bar */}
        <div className="flex items-center justify-end gap-2 mb-6">
          <Button variant="outline" onClick={fetchVendors}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={openNewVendor} data-testid="add-vendor-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Vendor
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className={`relative ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
            <HelpIcon elementId="vendor-card" position="top-right" />
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Store className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total Vendors</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`relative ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
            <HelpIcon elementId="vendor-favorites" position="top-right" />
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.favorites}</p>
                  <p className="text-sm text-muted-foreground">Favorites</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.withAccount}</p>
                  <p className="text-sm text-muted-foreground">With Account #</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs and Filters */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={darkMode ? 'bg-[#1A1A1A]' : ''}>
              <TabsTrigger value="all">All Vendors ({stats.total})</TabsTrigger>
              <TabsTrigger value="favorites" className="flex items-center gap-1">
                <Star className="w-3 h-3" />
                Favorites ({stats.favorites})
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search vendors..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className={`pl-9 w-48 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}
                data-testid="search-vendors"
              />
            </div>
            
            <Select value={specCategoryFilter} onValueChange={setSpecCategoryFilter}>
              <SelectTrigger className={`w-40 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`} data-testid="spec-category-filter">
                <SelectValue placeholder="Spec Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {specCategories.map(cat => (
                  <SelectItem key={cat.prefix} value={cat.prefix}>{cat.prefix} - {cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className={`w-32 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="company">Company</SelectItem>
                <SelectItem value="rating">Rating</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
            >
              {sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Vendors List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading vendors...</p>
          </div>
        ) : filteredVendors.length === 0 ? (
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardContent className="py-12 text-center">
              <Store className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No Vendors Found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? 'Try adjusting your search' : 'Add your first vendor to get started'}
              </p>
              <Button onClick={openNewVendor}>
                <Plus className="w-4 h-4 mr-2" />
                Add Vendor
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredVendors.map(vendor => (
              <Card 
                key={vendor.id} 
                className={`cursor-pointer transition-all hover:shadow-md ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#3A3A3A]' : 'hover:border-gray-300'}`}
                onClick={() => viewVendorDetail(vendor)}
                data-testid={`vendor-card-${vendor.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                        {getInitials(vendor)}
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>
                            {vendor.company || `${vendor.first_name} ${vendor.last_name}`}
                          </span>
                          {vendor.is_favorite_vendor && (
                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                          )}
                          {vendor.vendor_rating > 0 && renderStars(vendor.vendor_rating)}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                          {vendor.rep_name && (
                            <span className="flex items-center gap-1">
                              <UserCircle className="w-3 h-3" />
                              Rep: {vendor.rep_name}
                            </span>
                          )}
                          {vendor.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {vendor.email}
                            </span>
                          )}
                          {vendor.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {vendor.phone}
                            </span>
                          )}
                          {vendor.payment_terms && (
                            <Badge variant="outline" className="text-xs">
                              {vendor.payment_terms}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Spec Categories */}
                        {vendor.spec_categories?.length > 0 && (
                          <div className="flex items-center gap-1 mt-2">
                            {vendor.spec_categories.slice(0, 5).map(cat => (
                              <Badge key={cat} variant="secondary" className="text-xs px-1.5 py-0">
                                {cat}
                              </Badge>
                            ))}
                            {vendor.spec_categories.length > 5 && (
                              <span className="text-xs text-muted-foreground">+{vendor.spec_categories.length - 5}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => toggleFavorite(vendor)}
                        className={vendor.is_favorite_vendor ? 'text-amber-500' : 'text-muted-foreground'}
                        data-testid={`favorite-btn-${vendor.id}`}
                      >
                        <Star className={`w-4 h-4 ${vendor.is_favorite_vendor ? 'fill-amber-500' : ''}`} />
                      </Button>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewVendorDetail(vendor)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditVendor(vendor)}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleFavorite(vendor)}>
                            <Star className={`w-4 h-4 mr-2 ${vendor.is_favorite_vendor ? 'fill-amber-500 text-amber-500' : ''}`} />
                            {vendor.is_favorite_vendor ? 'Remove Favorite' : 'Add to Favorites'}
                          </DropdownMenuItem>
                          {vendor.website && (
                            <DropdownMenuItem onClick={() => window.open(vendor.website, '_blank')}>
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Visit Website
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => deleteVendor(vendor)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Vendor Dialog */}
      <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
        <DialogContent className={`max-w-3xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Company Info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Company Name *</Label>
                <Input
                  value={vendorForm.company}
                  onChange={e => setVendorForm(prev => ({ ...prev, company: e.target.value }))}
                  placeholder="Company name"
                  className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  data-testid="vendor-company"
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={vendorForm.website}
                  onChange={e => setVendorForm(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://..."
                  className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input
                  value={vendorForm.vendor_account_number}
                  onChange={e => setVendorForm(prev => ({ ...prev, vendor_account_number: e.target.value }))}
                  placeholder="Your account # with vendor"
                  className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                />
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <User className="w-4 h-4" />
                Contact Information
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={vendorForm.first_name}
                    onChange={e => setVendorForm(prev => ({ ...prev, first_name: e.target.value }))}
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={vendorForm.last_name}
                    onChange={e => setVendorForm(prev => ({ ...prev, last_name: e.target.value }))}
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
                <div>
                  <Label>Job Title</Label>
                  <Input
                    value={vendorForm.job_title}
                    onChange={e => setVendorForm(prev => ({ ...prev, job_title: e.target.value }))}
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={vendorForm.email}
                    onChange={e => setVendorForm(prev => ({ ...prev, email: e.target.value }))}
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={vendorForm.phone}
                    onChange={e => setVendorForm(prev => ({ ...prev, phone: e.target.value }))}
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <Input
                    value={vendorForm.phone_mobile}
                    onChange={e => setVendorForm(prev => ({ ...prev, phone_mobile: e.target.value }))}
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
              </div>
            </div>
            
            {/* Sales Rep */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <UserCircle className="w-4 h-4" />
                Sales Representative
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Rep Name</Label>
                  <Input
                    value={vendorForm.rep_name}
                    onChange={e => setVendorForm(prev => ({ ...prev, rep_name: e.target.value }))}
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
                <div>
                  <Label>Rep Email</Label>
                  <Input
                    type="email"
                    value={vendorForm.rep_email}
                    onChange={e => setVendorForm(prev => ({ ...prev, rep_email: e.target.value }))}
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
                <div>
                  <Label>Rep Phone</Label>
                  <Input
                    value={vendorForm.rep_phone}
                    onChange={e => setVendorForm(prev => ({ ...prev, rep_phone: e.target.value }))}
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
              </div>
            </div>
            
            {/* Address */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Address
              </h4>
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-4">
                  <Input
                    value={vendorForm.address_street}
                    onChange={e => setVendorForm(prev => ({ ...prev, address_street: e.target.value }))}
                    placeholder="Street address"
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    value={vendorForm.address_city}
                    onChange={e => setVendorForm(prev => ({ ...prev, address_city: e.target.value }))}
                    placeholder="City"
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
                <div>
                  <Input
                    value={vendorForm.address_state}
                    onChange={e => setVendorForm(prev => ({ ...prev, address_state: e.target.value }))}
                    placeholder="State"
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
                <div>
                  <Input
                    value={vendorForm.address_zip}
                    onChange={e => setVendorForm(prev => ({ ...prev, address_zip: e.target.value }))}
                    placeholder="ZIP"
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
              </div>
            </div>
            
            {/* Business Terms */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Business Terms
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Payment Terms</Label>
                  <Select 
                    value={vendorForm.payment_terms} 
                    onValueChange={v => setVendorForm(prev => ({ ...prev, payment_terms: v }))}
                  >
                    <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                      <SelectValue placeholder="Select terms" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map(term => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Preferred Shipping</Label>
                  <Select 
                    value={vendorForm.preferred_shipping} 
                    onValueChange={v => setVendorForm(prev => ({ ...prev, preferred_shipping: v }))}
                  >
                    <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {SHIPPING_METHODS.map(method => (
                        <SelectItem key={method} value={method}>{method}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Minimum Order ($)</Label>
                  <Input
                    type="number"
                    value={vendorForm.minimum_order}
                    onChange={e => setVendorForm(prev => ({ ...prev, minimum_order: e.target.value }))}
                    placeholder="0.00"
                    className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                  />
                </div>
              </div>
              <div>
                <Label>Return Policy Notes</Label>
                <Textarea
                  value={vendorForm.return_policy}
                  onChange={e => setVendorForm(prev => ({ ...prev, return_policy: e.target.value }))}
                  placeholder="Return policy details..."
                  rows={2}
                  className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                />
              </div>
            </div>
            
            {/* Spec Categories & Favorite */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Package className="w-4 h-4" />
                Categories & Rating
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Spec Categories</Label>
                  <p className="text-xs text-muted-foreground mb-2">Click to toggle categories this vendor supplies</p>
                  <div className="flex flex-wrap gap-1.5 p-2 rounded border min-h-[60px] bg-background">
                    {specCategories.map(cat => (
                      <Badge 
                        key={cat.prefix} 
                        variant={vendorForm.spec_categories.includes(cat.prefix) ? 'default' : 'outline'}
                        className="cursor-pointer transition-colors text-xs"
                        onClick={() => {
                          setVendorForm(prev => ({
                            ...prev,
                            spec_categories: prev.spec_categories.includes(cat.prefix)
                              ? prev.spec_categories.filter(c => c !== cat.prefix)
                              : [...prev.spec_categories, cat.prefix]
                          }));
                        }}
                      >
                        {cat.prefix}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Vendor Rating</Label>
                    <div className="flex gap-1 mt-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setVendorForm(prev => ({ ...prev, vendor_rating: star }))}
                          className="focus:outline-none"
                        >
                          <Star 
                            className={`w-6 h-6 transition-colors ${
                              star <= vendorForm.vendor_rating 
                                ? 'fill-amber-400 text-amber-400' 
                                : 'text-gray-300 hover:text-amber-300'
                            }`} 
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      id="is-favorite-vendor"
                      checked={vendorForm.is_favorite_vendor}
                      onCheckedChange={checked => setVendorForm(prev => ({ ...prev, is_favorite_vendor: checked }))}
                    />
                    <Label htmlFor="is-favorite-vendor" className="flex items-center gap-2 cursor-pointer">
                      <Star className={`w-4 h-4 ${vendorForm.is_favorite_vendor ? 'fill-amber-500 text-amber-500' : ''}`} />
                      Favorite Vendor
                    </Label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={vendorForm.notes}
                onChange={e => setVendorForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="General notes about this vendor..."
                rows={2}
                className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorDialog(false)}>Cancel</Button>
            <Button onClick={saveVendor} data-testid="save-vendor-btn">
              {editingVendor ? 'Update Vendor' : 'Add Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Detail Sheet */}
      <Sheet open={showDetailSheet} onOpenChange={setShowDetailSheet}>
        <SheetContent className={`w-[500px] sm:w-[600px] ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
          {selectedVendor && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {getInitials(selectedVendor)}
                  </div>
                  <div>
                    <span className="flex items-center gap-2">
                      {selectedVendor.company || `${selectedVendor.first_name} ${selectedVendor.last_name}`}
                      {selectedVendor.is_favorite_vendor && <Star className="w-4 h-4 fill-amber-400 text-amber-400" />}
                    </span>
                    {selectedVendor.vendor_rating > 0 && (
                      <div className="mt-1">{renderStars(selectedVendor.vendor_rating)}</div>
                    )}
                  </div>
                </SheetTitle>
              </SheetHeader>
              
              <div className="mt-6 space-y-6">
                {/* Contact Info */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Contact</h4>
                  {selectedVendor.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <a href={`mailto:${selectedVendor.email}`} className="text-blue-500 hover:underline">{selectedVendor.email}</a>
                    </div>
                  )}
                  {selectedVendor.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedVendor.phone}</span>
                    </div>
                  )}
                  {selectedVendor.website && (
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      <a href={selectedVendor.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{selectedVendor.website}</a>
                    </div>
                  )}
                </div>
                
                {/* Sales Rep */}
                {selectedVendor.rep_name && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Sales Rep</h4>
                    <div className="flex items-center gap-2">
                      <UserCircle className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedVendor.rep_name}</span>
                    </div>
                    {selectedVendor.rep_email && (
                      <div className="flex items-center gap-2 pl-6">
                        <Mail className="w-3 h-3 text-muted-foreground" />
                        <a href={`mailto:${selectedVendor.rep_email}`} className="text-sm text-blue-500 hover:underline">{selectedVendor.rep_email}</a>
                      </div>
                    )}
                    {selectedVendor.rep_phone && (
                      <div className="flex items-center gap-2 pl-6">
                        <Phone className="w-3 h-3 text-muted-foreground" />
                        <span className="text-sm">{selectedVendor.rep_phone}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Business Terms */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Business Terms</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {selectedVendor.vendor_account_number && (
                      <div>
                        <span className="text-muted-foreground">Account #:</span> {selectedVendor.vendor_account_number}
                      </div>
                    )}
                    {selectedVendor.payment_terms && (
                      <div>
                        <span className="text-muted-foreground">Terms:</span> {selectedVendor.payment_terms}
                      </div>
                    )}
                    {selectedVendor.preferred_shipping && (
                      <div>
                        <span className="text-muted-foreground">Shipping:</span> {selectedVendor.preferred_shipping}
                      </div>
                    )}
                    {selectedVendor.minimum_order && (
                      <div>
                        <span className="text-muted-foreground">Min Order:</span> ${selectedVendor.minimum_order}
                      </div>
                    )}
                  </div>
                  {selectedVendor.return_policy && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Return Policy:</span>
                      <p className="mt-1">{selectedVendor.return_policy}</p>
                    </div>
                  )}
                </div>
                
                {/* Spec Categories */}
                {selectedVendor.spec_categories?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Spec Categories</h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedVendor.spec_categories.map(cat => (
                        <Badge key={cat} variant="secondary">{cat}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Pricing History */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-muted-foreground">Pricing History</h4>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowPricingDialog(true)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Price
                    </Button>
                  </div>
                  {selectedVendor.pricing_history?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedVendor.pricing_history.slice(0, 5).map((entry, idx) => (
                        <div key={entry.id || idx} className="flex items-center justify-between text-sm p-2 rounded bg-background">
                          <div>
                            <span className="font-medium">{entry.clip_name}</span>
                            {entry.quote_number && <span className="text-muted-foreground ml-2">#{entry.quote_number}</span>}
                          </div>
                          <div className="text-right">
                            <span className="font-medium">${entry.price}</span>
                            <span className="text-muted-foreground">/{entry.unit}</span>
                            <div className="text-xs text-muted-foreground">{entry.date}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No pricing history yet</p>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button variant="outline" className="flex-1" onClick={() => openEditVendor(selectedVendor)}>
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button className="flex-1" onClick={() => window.location.href = `/purchase-orders?vendor=${selectedVendor.id}`}>
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Create PO
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add Pricing Dialog */}
      <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
        <DialogContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
          <DialogHeader>
            <DialogTitle>Add Pricing Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Product/Item Name</Label>
              <Input
                value={pricingForm.clip_name}
                onChange={e => setPricingForm(prev => ({ ...prev, clip_name: e.target.value }))}
                placeholder="e.g., 24x24 Porcelain Tile"
                className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Price</Label>
                <Input
                  type="number"
                  value={pricingForm.price}
                  onChange={e => setPricingForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0.00"
                  className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                />
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={pricingForm.unit} onValueChange={v => setPricingForm(prev => ({ ...prev, unit: v }))}>
                  <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="each">Each</SelectItem>
                    <SelectItem value="sf">SF</SelectItem>
                    <SelectItem value="lf">LF</SelectItem>
                    <SelectItem value="set">Set</SelectItem>
                    <SelectItem value="box">Box</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Quote #</Label>
              <Input
                value={pricingForm.quote_number}
                onChange={e => setPricingForm(prev => ({ ...prev, quote_number: e.target.value }))}
                placeholder="Quote reference"
                className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={pricingForm.notes}
                onChange={e => setPricingForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Pricing notes..."
                rows={2}
                className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPricingDialog(false)}>Cancel</Button>
            <Button onClick={addPricingEntry}>Add Pricing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
